import { ReactTFC } from "../../../../utils/fp/missing-ts-parts";
import React, { useEffect, useRef } from "react";

import { css } from "@emotion/react";

import { CSS_MQ } from "../../../../styles/breakpoints";
import { CSS_COLORS } from "../../../../styles/styles-css-variables";
import { ImageWithFocalPoint } from "../../../_shared-components/image-related/ImageWithFocalPoint";
import { SwarovskiLink } from "../../../_shared-components/ui-elements/SwarovskiLink";
import { FONTS } from "../../../../styles/fonts";
import { ExpandedStoryTeaser } from "../../../../layouts/stories/stories-overview-page/stories-overview-page-layout";
import { EXPERIMENTAL_TextWithProductNameRenderer } from "../../../../domain/swarovski-optik/products/product-name/product-name-renderer";
import { T } from "../../../T";

export const StoriesOverviewStickyTeaser: ReactTFC<ExpandedStoryTeaser> = props => {
  const { seoHeadline, emoHeadline, imageWithFocalPoint, link, readingTime, tags } = props;

  return (
    <div
      css={css`
        position: relative;
        height: 500px;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        margin-bottom: 40px;
        padding: 30px 20px;

        width: calc(100% + 40px);
        left: -20px;

        background-color: ${CSS_COLORS.Grey0};

        ${CSS_MQ.atLeastSm} {
          width: auto;
          height: 550px;
          position: sticky;
          top: 45px;
          left: 0;
          margin-bottom: 0;
        }
        ${CSS_MQ.atLeastMd} {
          padding: 30px 40px;
          height: 650px;
        }
        ${CSS_MQ.atLeastLg} {
          height: 780px;
        }
        ${CSS_MQ.atLeastXl} {
          height: 850px;
        }
        &:before {
          content: "";
          position: absolute;
          left: 0;
          bottom: 0;
          width: 100%;
          height: 200px;
          z-index: 1;
          background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.8));
        }
      `}
    >
      <SwarovskiLink link={link} styled={"no-decoration"} isInternal={true} scrollToTop={true}>
        <ImageWithFocalPoint
          {...imageWithFocalPoint}
          requestedWidth={1200}
          assetId={imageWithFocalPoint.image.sys.id}
        />

        <div
          css={css`
            color: ${CSS_COLORS.White};
            position: relative;
            text-align: center;
            z-index: 2;
          `}
        >
          <h2
            css={css`
              ${FONTS.SwarovskiFutura500};
              font-size: 17px;
              letter-spacing: 1px;
              text-transform: uppercase;
              margin-bottom: 8px;
            `}
          >
            <EXPERIMENTAL_TextWithProductNameRenderer text={seoHeadline} />
          </h2>
          <h3
            css={css`
              ${FONTS.MinervaModern500};

              font-size: 30px;
              line-height: 32px;
              letter-spacing: 1.5px;
              text-transform: uppercase;
              margin-top: 0;
              margin-bottom: 20px;
            `}
          >
            <EXPERIMENTAL_TextWithProductNameRenderer text={emoHeadline} />
          </h3>
        </div>
      </SwarovskiLink>
      <div
        css={css`
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          position: relative;
        `}
      >
        {tags.map((t, idx) => (
          <SwarovskiLink
            link={t.expandedLink}
            attachStyles={css`
              color: ${CSS_COLORS.White};
              margin-right: 5px;
              padding-right: 5px;
              font-weight: 500;
              line-height: 1;
              border-right: 1px solid ${CSS_COLORS.White};
              z-index: 2;
              &:last-child {
                border-right: none;
              }
            `}
            isInternal={true}
            scrollToTop={true}
            key={idx}
          >
            {t.label}
          </SwarovskiLink>
        ))}
      </div>

      <p
        css={css`
          color: ${CSS_COLORS.White};
          margin-top: 8px;
          font-size: 16px;
          font-weight: 300;
          line-height: 1;
          text-align: center;
          position: relative;
          z-index: 2;
        `}
      >
        <T>{readingTime}</T>
      </p>
    </div>
  );
};
